if (import.meta.env.PROD) {
  // Suppress all logs except errors
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.warn = () => {};
  // leave console.error intact if you want errors
}

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import { MainLayout } from './components/layout/MainLayout';
import { LoadingScreen } from './components/layout/LoadingScreen';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { useAuthStore } from './store/authStore';
import { analytics } from './utils/analytics';

// Lazy load components
const Splash = React.lazy(() => import('./pages/Splash/Splash'));
const Landing = React.lazy(() => import('./pages/Landing/Landing'));
const Auth = React.lazy(() => import('./pages/Auth/Auth'));
const UpdatePassword = React.lazy(() => import('./pages/Auth/UpdatePassword'));
const EmailVerification = React.lazy(() => import('./pages/Auth/EmailVerification'));
const Feed = React.lazy(() => import('./pages/Community/Feed'));
const Friends = React.lazy(() => import('./pages/Community/Friends'));
const Messages = React.lazy(() => import('./pages/Community/Messages'));
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const Goals = React.lazy(() => import('./pages/Dashboard/Goals'));
const Profile = React.lazy(() => import('./pages/Profile/Profile'));

// Initialize React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
      gcTime: 3600000,
      refetchOnWindowFocus: false,
    },
  },
});

// Analytics tracker component
const AnalyticsTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    analytics.trackPageView(location.pathname);
  }, [location]);

  return <>{children}</>;
};

// Protected route component (For Authorization)
interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, redirectTo = '/login' }) => {
  const { user } = useAuthStore();

  return user ? <>{children}</> : <Navigate to={redirectTo} replace />;
};

// Public route component that redirects authenticated users
const PublicRoute: React.FC<ProtectedRouteProps> = ({ children, redirectTo = '/dashboard' }) => {
  const { user } = useAuthStore();
  return !user ? <>{children}</> : <Navigate to={redirectTo} replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LanguageProvider>
            <NotificationProvider>
              <Router>
                <AnalyticsTracker>
                  <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                      {/* Splash Screen */}
                      <Route
                        path="/"
                        element={
                          <PublicRoute>
                            <Splash />
                          </PublicRoute>
                        }
                      />

                      {/* Landing Page */}
                      <Route
                        path="/landing"
                        element={
                          <PublicRoute>
                            <Landing />
                          </PublicRoute>
                        }
                      />

                      {/* Public Authentication Routes */}
                      <Route
                        path="/login"
                        element={
                          <PublicRoute>
                            <Auth mode="login" />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          <PublicRoute>
                            <Auth mode="register" />
                          </PublicRoute>
                        }
                      />
                      {/* Reset password: first, to send email */}
                      <Route
                        path="/reset-password"
                        element={
                          <PublicRoute>
                            <Auth mode="reset" />
                          </PublicRoute>
                        }
                      />
                      {/* Update password: when user clicks reset link in email */}
                      <Route path="/reset-password/:token" element={<UpdatePassword />} />

                      {/* Email verification route */}
                      <Route path="/verify-email" element={<EmailVerification />} />

                      {/* Protected Routes */}
                      <Route
                        element={
                          <ProtectedRoute>
                            <MainLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/dashboard/goals" element={<Goals />} />
                        <Route path="/community" element={<Feed />} />
                        <Route path="/community/friends" element={<Friends />} />
                        <Route path="/community/messages" element={<Messages />} />
                        <Route path="/profile" element={<Profile />} />
                      </Route>

                      {/* Default Route */}
                      <Route path="*" element={<Navigate to={useAuthStore.getState().user ? '/dashboard' : '/'} replace />} />
                    </Routes>
                  </Suspense>
                </AnalyticsTracker>
              </Router>
            </NotificationProvider>
          </LanguageProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
